import { useQuery } from "@apollo/client";
import RefreshIcon from "@mui/icons-material/Refresh";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import { useSnackbar } from "notistack";
import { ReactElement, useEffect, useRef } from "react";

import {
  GetNgpVanObjectRefreshJobQuery,
  GetNgpVanObjectRefreshJobQueryVariables,
  NgpVanDatabaseModeGqlType,
  NgpVanObjectRefreshJobFieldsFragment,
  NgpVanObjectType,
  RefreshNgpVanObjectsMutation,
  RefreshNgpVanObjectsMutationVariables,
  TrackedJobStatus,
} from "~frontend/__generated__/graphql";
import {
  GET_NGPVAN_REFRESH_JOB,
  REFRESH_NGPVAN_OBJECTS,
} from "~frontend/graphql/integration";
import theme from "~frontend/theme";
import { jobFailed, jobInProgress } from "~frontend/util/jobs";
import useMutation from "~frontend/util/useMutation";

export default function JobControls({
  lastRefreshTime,
  lastRefreshJob,
  integrationId,
  databaseMode,
  objectType,
  autoRefreshWindowMillis,
  refetchIntegration,
}: {
  lastRefreshTime: Date | null;
  lastRefreshJob: NgpVanObjectRefreshJobFieldsFragment | null;
  integrationId: string;
  databaseMode: NgpVanDatabaseModeGqlType;
  objectType: NgpVanObjectType;
  autoRefreshWindowMillis?: number;
  refetchIntegration: () => void;
}): ReactElement {
  const snackbar = useSnackbar();

  const [runRefreshJob, { loading: mutationRunning }] = useMutation<
    RefreshNgpVanObjectsMutation,
    RefreshNgpVanObjectsMutationVariables
  >(REFRESH_NGPVAN_OBJECTS, {
    variables: {
      input: {
        integrationId,
        databaseMode,
        refreshType: objectType,
      },
    },
    onErrorSnackbarMessage: "Error refreshing VAN data",
  });

  const jobRunning = lastRefreshJob && jobInProgress(lastRefreshJob);

  // This useEffect handle auto-refreshing if the job wasn't run recently
  const hasCheckedAutoRefresh = useRef(false);
  useEffect(() => {
    if (hasCheckedAutoRefresh.current) {
      return;
    }

    if (!autoRefreshWindowMillis) {
      return;
    }

    hasCheckedAutoRefresh.current = true;

    if (
      lastRefreshTime &&
      lastRefreshTime.getTime() + autoRefreshWindowMillis >=
        new Date().getTime()
    ) {
      // new enough
      return;
    }

    if (jobRunning) {
      // already running
      return;
    }

    // should refresh
    runRefreshJob().catch(e => {
      console.error("Error running VAN refresh job", e);
    });
  }, [autoRefreshWindowMillis, lastRefreshTime, jobRunning, runRefreshJob]);

  useQuery<
    GetNgpVanObjectRefreshJobQuery,
    GetNgpVanObjectRefreshJobQueryVariables
  >(GET_NGPVAN_REFRESH_JOB, {
    pollInterval: 1000,
    skip: !jobRunning,
    variables: {
      input: {
        trackedJobId: lastRefreshJob?.id ?? "",
      },
    },
  });

  // This useEffect handles the job completing: we report out status to the
  // user and refetch integration data
  const jobWasRunning = useRef(jobRunning);
  useEffect(() => {
    if (jobWasRunning.current && !jobRunning) {
      refetchIntegration();

      if (lastRefreshJob?.status === TrackedJobStatus.Done) {
        // success
        snackbar.enqueueSnackbar("VAN data refreshed", {
          variant: "success",
        });
      } else if (lastRefreshJob && jobFailed(lastRefreshJob).failed) {
        // failure
        snackbar.enqueueSnackbar(
          `Error refreshing VAN data: ${jobFailed(lastRefreshJob).errorMessage}`,
          {
            variant: "error",
          }
        );
      }
    }
    jobWasRunning.current = jobRunning;
  }, [jobRunning, refetchIntegration, snackbar, lastRefreshJob]);

  const loading = jobRunning || mutationRunning;

  let tooltipText;
  if (loading) {
    tooltipText = "Refreshing data from VAN...";
  } else if (lastRefreshTime) {
    tooltipText = `Last refreshed at ${lastRefreshTime.toLocaleString()}. Click to refresh VAN data`;
  } else {
    tooltipText = "Click to refresh VAN data";
  }

  return (
    <Tooltip title={tooltipText}>
      {/* We need the span because disable buttons don't fire hover events */}
      <span style={{ width: "fit-content" }}>
        <IconButton
          disabled={loading}
          onClick={() => {
            if (loading) {
              return;
            }
            runRefreshJob().catch(console.error);
          }}
        >
          {loading ? (
            <CircularProgress size={theme.spacing(3)} />
          ) : (
            <RefreshIcon />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
}
